import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';

import { LATEST_RELEASE_VERSION } from '../release-notes/release-notes.data';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const VERSION_URL = '/version.json';

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  public readonly updateAvailable = signal(false);

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    void this.checkForUpdate();

    interval(CHECK_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        void this.checkForUpdate();
      });

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void this.checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', onVisible);
    });
  }

  public activateUpdate(): void {
    window.location.reload();
  }

  public async checkForUpdate(): Promise<boolean> {
    try {
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) {
        return false;
      }

      const payload = (await response.json()) as { version?: unknown };
      const remoteVersion = typeof payload.version === 'string' ? payload.version.trim() : '';
      const available = remoteVersion !== '' && remoteVersion !== LATEST_RELEASE_VERSION;
      this.updateAvailable.set(available);
      return available;
    } catch {
      return false;
    }
  }
}
